// ============================================================================
// ChildSelector Component
// Dropdown/selector for choosing which child to view data for
// ============================================================================

import React from 'react';
import { useChildren } from '@/features/children/hooks/useChildren';
import { cn } from '@/core/lib/utils';

export interface Child {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  avatarUrl?: string;
}

interface ChildSelectorProps {
  selectedChildId?: string | null;
  onChildSelect: (childId: string) => void;
  className?: string;
  placeholder?: string;
  showAllOption?: boolean;
  allOptionLabel?: string;
}

export function ChildSelector({
  selectedChildId,
  onChildSelect,
  className,
  placeholder = 'Select a child',
  showAllOption = false,
  allOptionLabel = 'All Children',
}: ChildSelectorProps) {
  const { data: children, isLoading } = useChildren();

  if (isLoading) {
    return (
      <div className={cn('animate-pulse bg-gray-200 h-10 rounded-md w-48', className)} />
    );
  }

  const childList = children ?? [];

  return (
    <div className={cn('relative', className)}>
      <select
        value={selectedChildId ?? ''}
        onChange={(e) => onChildSelect(e.target.value)}
        className={cn(
          'block w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10',
          'text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
          'sm:text-sm'
        )}
      >
        {!selectedChildId && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        
        {showAllOption && (
          <option value="all">{allOptionLabel}</option>
        )}
        
        {childList.map((child) => (
          <option key={child.id} value={child.id}>
            {child.firstName} {child.lastName}
          </option>
        ))}
      </select>
    </div>
  );
}

// Card-style selector for more visual selection
interface ChildSelectorCardsProps {
  selectedChildId?: string | null;
  onChildSelect: (childId: string) => void;
  className?: string;
}

export function ChildSelectorCards({
  selectedChildId,
  onChildSelect,
  className,
}: ChildSelectorCardsProps) {
  const { data: children, isLoading } = useChildren();

  if (isLoading) {
    return (
      <div className={cn('flex gap-3', className)}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-gray-200 h-16 w-32 rounded-lg" />
        ))}
      </div>
    );
  }

  const childList = children ?? [];

  if (childList.length === 0) {
    return (
      <div className={cn('text-gray-500 text-sm', className)}>
        No children added yet.
      </div>
    );
  }

  return (
    <div className={cn('flex flex-wrap gap-3', className)}>
      {childList.map((child) => {
        const isSelected = selectedChildId === child.id;
        const initials = `${child.firstName[0]}${child.lastName[0]}`.toUpperCase();
        
        return (
          <button
            key={child.id}
            onClick={() => onChildSelect(child.id)}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all',
              'hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              isSelected
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            )}
          >
            {child.avatarUrl ? (
              <img
                src={child.avatarUrl}
                alt={`${child.firstName}'s avatar`}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium',
                isSelected
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              )}>
                {initials}
              </div>
            )}
            <div className="text-left">
              <div className={cn(
                'font-medium',
                isSelected ? 'text-primary-700' : 'text-gray-900'
              )}>
                {child.firstName}
              </div>
              <div className="text-xs text-gray-500">
                {child.lastName}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default ChildSelector;
